import React, { useState } from "react"
import { Button, Divider, Empty, Form, List, Select, Space, notification } from "antd"
import { DeleteOutlined } from "@ant-design/icons"

import {
    createAmoPrerequisiteRule,
    deleteAmoPrerequisiteRule,
} from "../../api/checklists"
import { getAllAmoFields } from "../../utils/amoFieldsUtils"

const getAvailableAmoFields = () => getAllAmoFields()

const getAmoFieldTypeLabel = (type) => {
    const normalizedType = String(type || "").toUpperCase()
    const labels = {
        TEXT: "Текст",
        TEXTAREA: "Текст",
        SELECT: "Селектор",
        MULTISELECT: "Мультисписок",
        DATE: "Дата",
        URL: "Ссылка",
        PHONE: "Телефон",
        EMAIL: "Email",
        CHECKBOX: "Чекбокс",
    }

    return labels[normalizedType] || normalizedType || "Поле"
}

const getRuleSummary = (rule, amoFieldsById, customFieldsById) => {
    const amoFieldNames = (rule.prerequisite_amo_field_ids || []).map(fieldId =>
        amoFieldsById[String(fieldId)]?.name || `AMO поле ${fieldId}`
    )
    const blockedFieldNames = (rule.blocked_custom_field_ids || []).map(fieldId =>
        customFieldsById[fieldId]?.name || `Поле ${fieldId}`
    )

    return `Пока не заполнены: ${amoFieldNames.join(", ")}; заблокировать: ${blockedFieldNames.join(", ")}`
}

/**
 * Редактор правил блокировки полей чеклиста по заполненности AMO-полей.
 *
 * Компонент работает только с настройками выбранного чеклиста и статуса:
 * создает/удаляет правила на backend и синхронно обновляет локальное состояние,
 * чтобы менеджер сразу видел итоговую конфигурацию без перезагрузки окна.
 */
const AmoPrerequisiteRulesEditor = ({
    statusId,
    editingChecklist,
    setEditingChecklist,
    checklists,
    setChecklists,
}) => {
    const [selectedAmoFieldIds, setSelectedAmoFieldIds] = useState([])
    const [selectedBlockedFieldIds, setSelectedBlockedFieldIds] = useState([])
    const [loading, setLoading] = useState(false)

    const availableAmoFields = getAvailableAmoFields()
    const amoFieldsById = availableAmoFields.reduce((acc, field) => {
        acc[String(field.id)] = field
        return acc
    }, {})
    const customFields = editingChecklist?.custom_fields || []
    const customFieldsById = customFields.reduce((acc, field) => {
        acc[field.id] = field
        return acc
    }, {})
    const prerequisiteRules = editingChecklist?.amo_prerequisite_rules || []

    const syncChecklistRules = (nextRules) => {
        const nextChecklist = {
            ...editingChecklist,
            amo_prerequisite_rules: nextRules,
        }
        setEditingChecklist(nextChecklist)
        setChecklists(
            checklists.map(checklist =>
                checklist.id === nextChecklist.id ? nextChecklist : checklist
            )
        )
    }

    const resetForm = () => {
        setSelectedAmoFieldIds([])
        setSelectedBlockedFieldIds([])
    }

    const handleCreateRule = async () => {
        if (selectedAmoFieldIds.length === 0 || selectedBlockedFieldIds.length === 0) {
            return
        }

        setLoading(true)
        try {
            const createdRule = await createAmoPrerequisiteRule(
                statusId,
                editingChecklist.id,
                {
                    prerequisite_amo_field_ids: selectedAmoFieldIds,
                    blocked_custom_field_ids: selectedBlockedFieldIds,
                }
            )

            if (!createdRule?.id) {
                throw new Error("Не удалось создать правило")
            }

            syncChecklistRules([...prerequisiteRules, createdRule])
            resetForm()
        } catch (error) {
            notification.error({
                message: "Ошибка",
                description: "Не удалось сохранить правило блокировки по AMO",
                placement: "bottomRight",
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteRule = async (ruleId) => {
        setLoading(true)
        try {
            const success = await deleteAmoPrerequisiteRule(
                statusId,
                editingChecklist.id,
                ruleId
            )
            if (!success) {
                throw new Error("Не удалось удалить правило")
            }

            syncChecklistRules(
                prerequisiteRules.filter(rule => rule.id !== ruleId)
            )
        } catch (error) {
            notification.error({
                message: "Ошибка",
                description: "Не удалось удалить правило блокировки по AMO",
                placement: "bottomRight",
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <List
                bordered
                loading={loading}
                dataSource={prerequisiteRules}
                locale={{ emptyText: <Empty description="Нет правил блокировки по AMO" /> }}
                style={{ maxHeight: "220px", overflowY: "auto" }}
                renderItem={(rule) => (
                    <List.Item
                        actions={[
                            <Button
                                key={`delete-${rule.id}`}
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                                onClick={() => handleDeleteRule(rule.id)}
                            />,
                        ]}
                    >
                        {getRuleSummary(rule, amoFieldsById, customFieldsById)}
                    </List.Item>
                )}
            />

            <Divider style={{ margin: "12px 0" }} />

            <Form layout="vertical">
                <Form.Item label="AMO-поля, которые должны быть заполнены">
                    <Select
                        mode="multiple"
                        placeholder="Выберите одно или несколько AMO-полей"
                        value={selectedAmoFieldIds}
                        onChange={setSelectedAmoFieldIds}
                        disabled={loading}
                        options={availableAmoFields.map(field => ({
                            label: `${field.name} (${getAmoFieldTypeLabel(field.type)})`,
                            value: field.id,
                        }))}
                    />
                </Form.Item>

                <Form.Item label="Какие поля чеклиста заблокировать">
                    <Select
                        mode="multiple"
                        placeholder="Выберите поля текущего чеклиста"
                        value={selectedBlockedFieldIds}
                        onChange={setSelectedBlockedFieldIds}
                        disabled={loading || customFields.length === 0}
                        options={customFields.map(field => ({
                            label: field.name,
                            value: field.id,
                        }))}
                    />
                </Form.Item>

                <Button
                    type="primary"
                    block
                    onClick={handleCreateRule}
                    disabled={
                        loading ||
                        selectedAmoFieldIds.length === 0 ||
                        selectedBlockedFieldIds.length === 0
                    }
                >
                    Добавить правило
                </Button>
            </Form>
        </Space>
    )
}

export default AmoPrerequisiteRulesEditor
